import { useMemo } from "react";
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import {
  createExecutiveAccess,
  deleteExecutiveAccess,
  getExecutiveAccess,
  updateExecutiveAccess,
} from "apps/project-iq/api";
import {
  IExecutiveAccess,
  IExecutiveAccessInput,
} from "apps/project-iq/models/IExecutiveAccess";

export const EXECUTIVE_ACCESS_QUERY_KEY = "executiveAccess";

export interface IUseExecutiveAccessResult {
  executiveAccessList: IExecutiveAccess[];
  isLoading: boolean;
  isError: boolean;
  createMutation: UseMutationResult<
    IExecutiveAccess,
    Error,
    IExecutiveAccessInput
  >;
  updateMutation: UseMutationResult<IExecutiveAccess, Error, IExecutiveAccess>;
  deleteMutation: UseMutationResult<unknown, Error, string>;
}

export const useExecutiveAccess = (): IUseExecutiveAccessResult => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<IExecutiveAccess[], Error>({
    queryKey: [EXECUTIVE_ACCESS_QUERY_KEY],
    queryFn: () => getExecutiveAccess(),
  });

  const executiveAccessList = useMemo(() => data ?? [], [data]);

  const createMutation = useMutation<
    IExecutiveAccess,
    Error,
    IExecutiveAccessInput
  >((input: IExecutiveAccessInput) => createExecutiveAccess(input), {
    onSuccess: () => {
      queryClient.invalidateQueries([EXECUTIVE_ACCESS_QUERY_KEY]);
    },
  });

  const updateMutation = useMutation<IExecutiveAccess, Error, IExecutiveAccess>(
    ({ id, user }: IExecutiveAccess) => updateExecutiveAccess(id, { user }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([EXECUTIVE_ACCESS_QUERY_KEY]);
      },
    },
  );

  const deleteMutation = useMutation<unknown, Error, string>(
    (id: string) => deleteExecutiveAccess(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([EXECUTIVE_ACCESS_QUERY_KEY]);
      },
    },
  );

  return {
    executiveAccessList,
    isLoading,
    isError,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};
